// ---------- briques à tenons : vingt-trois couleurs, une brique par case ----------
//   0        case vide = papier nu (le fond détouré, les blancs purs)
//   1..23    une brique de la couleur BRICKS[code - 1], vue de dessus avec son tenon
//
// D'OÙ IL VIENT. La planche « Matières et lumières » (2026-10-12) l'a mesuré parmi dix alphabets :
// le plus fidèle avec Tricot (écart 1,3 à 3,7 selon le sujet), jugé alors « tout le monde connaît
// cette mosaïque ». Retenu le 2026-10-14 à la réflexion — c'est justement parce que tout le monde
// la connaît qu'elle se lit d'un coup d'œil.
//
// LA PALETTE N'EST PAS CELLE DE LA PLANCHE. Là-bas, dix-huit couleurs apprises par k-means sur
// huit tableaux : la palette suivait leurs teintes, et ce qui n'y était pas (le cyan, le lime, le
// rose) manquait. Ici, vingt-trois couleurs posées À LA MAIN pour couvrir toute la roue, mesurées
// en OKLCH le 2026-10-14 :
//   - cinq neutres du noir au blanc (clarté 0,23 → 0,96) : une mosaïque est d'abord faite de
//     valeurs, et c'est le noir qui apporte les ombres (cf. la leçon du fil noir de Tissage) ;
//   - dix-huit teintes dont le plus grand trou sur la roue est de 36° (marine → violet). Une
//     première version en laissait 53° entre turquoise et bleu ciel : l'azur l'a comblé ;
//   - chaque grande famille en deux clartés au moins (bleu ciel / bleu / marine, lime / vert /
//     vert sapin, chair / orange / brun, lavande / violet), parce que la diffusion d'erreur
//     mélange les VALEURS voisines mieux que les teintes lointaines.
// La paire la plus proche (gris clair / chair, 0,077 en OKLab) reste distincte à l'œil : l'une
// est froide, l'autre rose.
//
// POURQUOI UN ID NEUF ET PAS UN HABILLAGE. La planche concluait « un alphabet, cinq habillages »
// (Tricot, Écailles, Briques… sur les mêmes codes). Un habillage au choix du joueur demanderait
// de stocker l'habillage à part et de le régler partout où la feuille se dessine ; un alphabet
// à part ne demande que ce fichier. Si les autres habillages viennent un jour, ils pourront
// partager BRICKS par import, sans toucher aux grilles déjà publiées.

import { rgbOf } from '../ink';
import type { Alphabet } from './types';

/** Exportée pour `briquesNuancees`, dont le nuancier commence par ces vingt-trois-là, mêmes codes,
 *  mêmes noms. */
export const BRICKS = [
  { css: '#1c1c20', name: 'noir' },
  { css: '#4a4e57', name: 'gris anthracite' },
  { css: '#8b9098', name: 'gris' },
  { css: '#c8cbd0', name: 'gris clair' },
  { css: '#f6f3ea', name: 'blanc' },
  { css: '#7c1d2a', name: 'bordeaux' },
  { css: '#cc2a2e', name: 'rouge' },
  { css: '#ee7b1e', name: 'orange' },
  { css: '#6a3f25', name: 'brun' },
  { css: '#f0c4a0', name: 'chair' },
  { css: '#f6c71c', name: 'jaune' },
  { css: '#a2c537', name: 'vert lime' },
  { css: '#2f923e', name: 'vert' },
  { css: '#1c4d2e', name: 'vert sapin' },
  { css: '#1ea39a', name: 'turquoise' },
  { css: '#0aa3c2', name: 'azur' },
  { css: '#86c2ec', name: 'bleu ciel' },
  { css: '#2a55b0', name: 'bleu' },
  { css: '#1b2b5e', name: 'bleu marine' },
  { css: '#6b3e9c', name: 'violet' },
  { css: '#b49ad8', name: 'lavande' },
  { css: '#a8379b', name: 'magenta' },
  { css: '#e0569a', name: 'rose' },
];

// ---------- les tons d'une brique ----------
// Bord, ombre du tenon, reflet : la couleur décalée en CLARTÉ OKLab, teinte gardée — la même
// opération que `shade()` dans la planche. Calculés une fois par couleur et non à chaque case :
// une feuille de 150 cases en dessine plus de 20 000, et la conversion coûte deux racines
// cubiques par canal.

function toLin(c: number): number {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function toSrgb(c: number): number {
  c = Math.max(0, Math.min(1, c));
  return Math.round(255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055));
}

/** `css` éclairci (k > 0) ou assombri (k < 0) de `k` en clarté OKLab. */
function shade(css: string, k: number): string {
  const [R, G, B] = rgbOf(css).map(toLin);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  const L = Math.max(0, Math.min(1, 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s + k));
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const b = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const l2 = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m2 = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s2 = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const r = toSrgb(4.0767416621 * l2 - 3.3077115913 * m2 + 0.2309699292 * s2);
  const g = toSrgb(-1.2684380046 * l2 + 2.6097574011 * m2 - 0.3413193965 * s2);
  const bb = toSrgb(-0.0041960863 * l2 - 0.7034186147 * m2 + 1.707614701 * s2);
  return `rgb(${r},${g},${bb})`;
}

export interface BrickTones {
  body: string;
  edge: string;
  drop: string;
  top: string;
  shine: string;
}

/** Les tons d'une brique de couleur `css`. Exportée pour `briquesNuancees` : les deux alphabets
 *  posent exactement la même brique, seul change le nuancier. */
export function brickTones(css: string): BrickTones {
  return {
    body: css,
    edge: shade(css, -0.12),
    drop: shade(css, -0.16),
    top: shade(css, 0.04),
    shine: shade(css, 0.2),
  };
}

let tones: BrickTones[] | null = null;

function tonesOf(code: number): BrickTones {
  if (!tones) tones = BRICKS.map(({ css }) => brickTones(css));
  return tones[code - 1];
}

/**
 * Une brique vue de dessus : le corps, un liseré plus sombre qui la détache de ses voisines, le
 * tenon (son ombre portée vers le bas-droite, sa face, un reflet en arc en haut-gauche).
 *
 * Plus simple que la planche, qui posait un dégradé linéaire sur chaque tenon : un dégradé par
 * case, c'est un objet alloué par case, et la feuille se redessine pendant les gestes. Le reflet
 * en arc porte à lui seul l'idée de relief ; à la taille d'une case de jeu le dégradé ne se voyait
 * pas.
 *
 * Le joint de 3 % de chaque côté laisse voir le papier entre les briques : c'est ce qui fait une
 * mosaïque et non un aplat, et il est compté dans la couleur effective (cf. measure).
 */
function drawBrick(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  code: number,
): void {
  paintBrick(ctx, x, y, w, h, tonesOf(code));
}

/** La brique elle-même, de tons donnés — partagée avec `briquesNuancees`. */
export function paintBrick(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  t: BrickTones,
): void {
  const s = Math.min(w, h);
  const g = s * 0.03;
  ctx.fillStyle = t.body;
  ctx.fillRect(x + g, y + g, w - 2 * g, h - 2 * g);
  ctx.strokeStyle = t.edge;
  ctx.lineWidth = s * 0.04;
  ctx.strokeRect(x + g * 1.5, y + g * 1.5, w - 3 * g, h - 3 * g);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const r = s * 0.3;
  ctx.fillStyle = t.drop;
  ctx.beginPath();
  ctx.arc(cx + s * 0.035, cy + s * 0.05, r, 0, 7);
  ctx.fill();
  ctx.fillStyle = t.top;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, 7);
  ctx.fill();
  ctx.strokeStyle = t.shine;
  ctx.lineWidth = s * 0.035;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.78, Math.PI * 1.05, Math.PI * 1.55);
  ctx.stroke();
}

// ---------- la couleur EFFECTIVE de chaque brique ----------
// Le liseré, l'ombre du tenon et les joints de papier assombrissent la brique : sa couleur vue
// de loin n'est pas `css`. L'encodeur apparie donc contre ce que la case rend vraiment, comme
// pointilleRiche (même mesure, même voile de .sheetwrap sur le papier).

export interface EffectiveBrick {
  r: number;
  g: number;
  b: number;
}

let effective: EffectiveBrick[] | null = null;
let paperLum = 0;

function measure(): void {
  const m = measureBricks(BRICKS.length, (ctx, x, y, s, code) => drawBrick(ctx, x, y, s, s, code));
  effective = m.effective;
  paperLum = m.paperLum;
}

/**
 * Les couleurs EFFECTIVES de `count` briques (papier compris, au code 0) et la luminance du
 * papier, en dessinant chaque code en plaque 3 × 3 sur le papier du jeu. Partagée avec
 * `briquesNuancees`, qui mesure ainsi ses 256 couleurs (~25 ms).
 */
export function measureBricks(
  count: number,
  draw: (ctx: CanvasRenderingContext2D, x: number, y: number, s: number, code: number) => void,
): { effective: EffectiveBrick[]; paperLum: number } {
  const S = 24;
  const n = 3;
  const P = S * n; // le côté d'une plaque
  // TOUTES LES PLAQUES SUR UNE TOILE, UNE SEULE RELECTURE (revue du 2026-10-18). On relisait le
  // canvas une fois par code : 23 fois pour `briques`, mais 256 pour `briquesNuancees`, et sur une
  // WebView qui dessine par le processeur graphique chaque getImageData attend la fin du dessin —
  // c'est sur un téléphone, dans « Créer », que la première conversion le paie. Les plaques sont
  // posées à des positions entières et une brique ne déborde pas de sa case : chacune se rastérise
  // exactement comme seule sur sa toile, et les couleurs mesurées ne bougent pas.
  const cols = Math.min(count, 16);
  const rows = Math.ceil(count / cols);
  const c = document.createElement('canvas');
  c.width = P * cols;
  c.height = P * rows;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  const paperCss =
    getComputedStyle(document.documentElement).getPropertyValue('--paper').trim() || '#fbf6ec';
  const [pr, pg, pb] = rgbOf(paperCss);
  // Le papier vu sous le canvas est assombri de 4 % par le voile de .sheetwrap (cf. pointille.ts).
  const p0 = { r: pr * 0.96, g: pg * 0.96, b: pb * 0.96 };
  ctx.fillStyle = paperCss;
  ctx.fillRect(0, 0, c.width, c.height);
  for (let code = 1; code <= count; code++) {
    const ox = ((code - 1) % cols) * P;
    const oy = Math.floor((code - 1) / cols) * P;
    for (let r = 0; r < n; r++)
      for (let q = 0; q < n; q++) draw(ctx, ox + q * S, oy + r * S, S, code);
  }
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  const entries: EffectiveBrick[] = [p0];
  for (let code = 1; code <= count; code++) {
    const ox = ((code - 1) % cols) * P;
    const oy = Math.floor((code - 1) / cols) * P;
    let R = 0;
    let G = 0;
    let B = 0;
    for (let y = oy; y < oy + P; y++) {
      for (let i = (y * c.width + ox) * 4, end = i + P * 4; i < end; i += 4) {
        R += d[i];
        G += d[i + 1];
        B += d[i + 2];
      }
    }
    const m = P * P;
    entries.push({ r: R / m, g: G / m, b: B / m });
  }
  return { effective: entries, paperLum: 0.299 * p0.r + 0.587 * p0.g + 0.114 * p0.b };
}

/** Papier (code 0) puis les vingt-trois briques telles qu'elles se voient, pour briques.encode.ts. */
export function getEffectiveBricks(): EffectiveBrick[] {
  if (!effective) measure();
  return effective!;
}

export function paperLuminanceBriques(): number {
  if (!effective) measure();
  return paperLum;
}

/** L'ordre de lecture de la palette, par nom de couleur (cf. paletteOrder plus bas). */
const PALETTE_ORDER = [
  'rouge',
  'orange',
  'jaune',
  'vert lime',
  'vert',
  'vert sapin',
  'turquoise',
  'azur',
  'bleu ciel',
  'bleu',
  'bleu marine',
  'lavande',
  'violet',
  'magenta',
  'rose',
  'bordeaux',
  'chair',
  'brun',
  'blanc',
  'gris clair',
  'gris',
  'gris anthracite',
  'noir',
];

/** Le nombre de couleurs, pour les tests et l'encodeur. */
export const BRICK_COUNT = BRICKS.length;

export const briques: Alphabet = {
  id: 'briques',
  sheetMode: 'single',
  bakesOnTop: true,
  label: 'Briques',
  symbolName: 'brique',
  selfColored: true,
  helpText:
    'Une mosaïque de briques à tenons, vue de dessus : une brique par case, en vingt-trois ' +
    'couleurs réparties sur toute la roue, du noir au blanc. Le fond reste du papier nu.',

  aspect: () => 1,

  // L'ORDRE DE LA PALETTE, qui est aussi celui dans lequel la feuille propose ses briques (le
  // symbole présélectionné à l'ouverture, cf. Alphabet.paletteOrder). Les couleurs vives d'abord,
  // dans l'ordre de l'arc-en-ciel, puis les tons de terre, puis les neutres du clair au foncé —
  // le NOIR EN DERNIER, comme le symbole le plus couvrant des autres alphabets.
  //
  // C'était l'inverse (les neutres du noir au blanc, puis la roue), et ça s'est vu dès le premier
  // essai (2026-10-14) : sur un sujet à fond sombre, la feuille s'ouvrait sur le grand fond noir.
  // Le moins agréable à peindre, et celui qui livre la silhouette d'un coup. Le joueur commence
  // maintenant par les couleurs du sujet et finit par le fond.
  paletteOrder: PALETTE_ORDER.map((name) => BRICKS.findIndex((b) => b.name === name) + 1),

  codeName: (code) => BRICKS[code - 1]?.name ?? '',

  draw(ctx, x, y, w, h, code) {
    if (!code || code > BRICKS.length) return;
    drawBrick(ctx, x, y, w, h, code);
  },
};
